import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { fromEvent, startWith, Subject, takeUntil } from 'rxjs';

export interface TimeSlot {
  endTime: string;
  id: number;
  startTime: string;
}

export interface AppointmentDay {
  date: string;
  appointments: Array<TimeSlot> | null;
}

@Component({
  selector: 'ui-dates-carousel',
  templateUrl: './dates-carousel.component.html',
  styleUrls: ['./dates-carousel.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatesCarouselComponent implements AfterContentInit {
  @Input() public gtmTag = '';
  @Output() public bookedTimeSlot: EventEmitter<TimeSlot> =
    new EventEmitter<TimeSlot>();
  @ViewChild('carousel', { read: ElementRef, static: false })
  private _carousel: ElementRef<HTMLDivElement> | undefined;

  private _destroy$: Subject<void> = new Subject<void>();
  private _availableDates: Array<AppointmentDay> | null = null;

  public selectedDate: AppointmentDay | null = null;
  public selectedTimeSlot: TimeSlot | null = null;
  public isScrollActive = {
    left: false,
    right: false,
  };

  constructor(private _cdRef: ChangeDetectorRef) {}

  public get availableDates(): Array<AppointmentDay> | null {
    return this._availableDates;
  }

  @Input()
  public set availableDates(data: Array<AppointmentDay> | null) {
    this._availableDates = data;
    this._updateScrollButtonsActiveClass();
  }

  public trackByDate(index: number, item: AppointmentDay): string {
    return item.date;
  }

  public trackById(index: number, { id }: TimeSlot): number {
    return id;
  }

  public selectDay(date: AppointmentDay): void {
    this.selectedTimeSlot = null;
    this.selectedDate = date === this.selectedDate ? null : date;
  }

  public selectTimeSlot(timeslot: TimeSlot): void {
    this.selectedTimeSlot =
      this.selectedTimeSlot &&
      this.selectedTimeSlot.startTime === timeslot.startTime
        ? null
        : timeslot;
  }

  public scroll(direction: 'left' | 'right'): void {
    const scrollAmount = 400;
    const elementScrollLeft = this._carousel?.nativeElement.scrollLeft;
    if (typeof elementScrollLeft === 'number' && this._carousel) {
      const left =
        direction === 'left'
          ? elementScrollLeft - scrollAmount
          : elementScrollLeft + scrollAmount;
      this._carousel.nativeElement.scrollTo({ left, behavior: 'smooth' });
    }
  }

  public submitTimeSlot(slot: TimeSlot | null): void {
    if (!slot) {
      return;
    }

    this.bookedTimeSlot.emit(slot);
  }

  public ngAfterContentInit(): void {
    this._carousel?.nativeElement?.querySelector('.active')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'start',
    });
  }

  private _updateScrollButtonsActiveClass(): void {
    this._cdRef.detectChanges();

    if (this._carousel) {
      fromEvent(this._carousel.nativeElement, 'scroll')
        .pipe(
          startWith({ target: this._carousel.nativeElement }),
          takeUntil(this._destroy$),
        )
        .subscribe((event) => {
          const target = event.target as HTMLDivElement;
          const { scrollLeft, scrollWidth, clientWidth } = target;
          this.isScrollActive.left = scrollLeft > 0;
          this.isScrollActive.right = scrollLeft !== scrollWidth - clientWidth;
          this._cdRef.detectChanges();
        });
    }
  }
}
